import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LiveProvider } from "./context/LiveContext";
import { AppShell } from "./components/AppShell";
import { ErrorState, Loading } from "./components/common";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import { PartnersList, PartnerView } from "./pages/Partners";
import Checks from "./pages/Checks";
import Admin from "./pages/Admin";
import Drawdowns from "./pages/Drawdowns";
import DrawdownEditor from "./pages/DrawdownEditor";
import Inbox from "./pages/Inbox";
import EmailAction from "./pages/EmailAction";
import { Apply, Join } from "./pages/PublicOnboarding";
import PartnerOnboarding from "./pages/PartnerOnboarding";
import Board from "./pages/Board";
import NewOpportunity from "./pages/NewOpportunity";
import Pipeline from "./pages/Pipeline";
import Quarter from "./pages/Quarter";
import Insight from "./pages/Insight";
import FillGaps from "./pages/FillGaps";
import MyVariance from "./pages/MyVariance";
import Approvals from "./pages/Approvals";
import Help from "./pages/Help";
import { BaselinesTab } from "./components/admin/RecordsTabs";
import { PageHeader } from "./components/common";
import ReturnPage, { ReturnsList } from "./pages/Returns";

function Protected() {
  const { user } = useAuth();
  if (user === null) return <Loading label="Signing you in…" />;
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell />;
}

function EpilOnly({ children }) {
  const { isEpil } = useAuth();
  return isEpil ? children : <ErrorState message="You don't have access to this" status={403} />;
}

function Home() {
  const { isPartner, user } = useAuth();
  if (isPartner && (user.partner_status || "Active") !== "Active") return <Navigate to={`/onboarding/partners/${user.partner_id}`} replace />;
  if (isPartner) return <Navigate to={`/partners/${user.partner_id}`} replace />;
  if (user.roles.some((r) => ["Approver", "Senior stakeholder"].includes(r))) return <Insight home />;
  return <Dashboard />;
}

function ThemedToaster() {
  const { theme } = useAuth();
  return <Toaster theme={theme} position="bottom-right" richColors />;
}

export default function App() {
  return (
    <AuthProvider>
      <LiveProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/email-action/:token" element={<EmailAction />} />
            <Route path="/apply" element={<Apply />} />
            <Route path="/join/:token" element={<Join />} />
            <Route element={<Protected />}>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/partners" element={<EpilOnly><PartnersList /></EpilOnly>} />
              <Route path="/partners/:id" element={<PartnerView />} />
              <Route path="/checks" element={<EpilOnly><Checks /></EpilOnly>} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/drawdowns" element={<Drawdowns />} />
              <Route path="/drawdowns/new" element={<DrawdownEditor />} />
              <Route path="/drawdowns/:id" element={<DrawdownEditor key={window.location.pathname} />} />
              <Route path="/inbox" element={<EpilOnly><Inbox /></EpilOnly>} />
              <Route path="/onboarding" element={<EpilOnly><Board /></EpilOnly>} />
              <Route path="/onboarding/partners/:id" element={<PartnerOnboarding />} />
              <Route path="/opportunities/new" element={<NewOpportunity />} />
              <Route path="/pipeline/:id" element={<Pipeline />} />
              <Route path="/quarter" element={<Quarter />} />
              <Route path="/insight" element={<Insight />} />
              <Route path="/gaps" element={<FillGaps />} />
              <Route path="/my-variance" element={<MyVariance />} />
              <Route path="/my-variance/:pid" element={<MyVariance />} />
              <Route path="/approvals" element={<EpilOnly><Approvals /></EpilOnly>} />
              <Route path="/dashboard" element={<EpilOnly><Dashboard /></EpilOnly>} />
              <Route path="/baselines" element={<EpilOnly><div className="space-y-6"><PageHeader eyebrow="Proformas" title="Baseline review" /><BaselinesTab /></div></EpilOnly>} />
              <Route path="/returns" element={<ReturnsList />} />
              <Route path="/returns/:id" element={<ReturnPage />} />
              <Route path="/help" element={<Help />} />
              <Route path="/help/:section" element={<Help />} />
              <Route path="*" element={<ErrorState message="Page not found" />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <ThemedToaster />
      </LiveProvider>
    </AuthProvider>
  );
}
